import type { ClusterMatrix } from '../../data/schemas';
import { CLUSTER_IDS, QUESTION_IDS, type ClusterId } from '../../types/ids';
import type { ContributingQuestion } from '../../types/result';
import type { QuestionAnswers } from '../../types/session';

export interface ClusterRawResult {
  clusterId: ClusterId;
  rawScore: number;
  maximum: number;
  percentage: number;
  contributingQuestions: ContributingQuestion[];
}

/**
 * Berechnet die sechs Cluster-Rohwerte aus den Antworten und der Gewichtsmatrix (Abschnitt 16.3).
 * Verändert weder Antworten noch Matrix (DAT-003) und beeinflusst den Gesamt-Score nicht.
 */
export function calculateClusterScores(
  answers: QuestionAnswers,
  matrix: ClusterMatrix,
): Record<ClusterId, ClusterRawResult> {
  const result = {} as Record<ClusterId, ClusterRawResult>;

  for (const clusterId of CLUSTER_IDS) {
    let rawScore = 0;
    let maximum = 0;
    const contributingQuestions: ContributingQuestion[] = [];

    for (const questionId of QUESTION_IDS) {
      const weight = matrix.rows[questionId]?.[clusterId] ?? 0;
      if (weight <= 0) continue;
      maximum += 2 * weight;
      const answer = answers[questionId];
      if (!answer) continue;
      const weightedScore = answer.score * weight;
      rawScore += weightedScore;
      contributingQuestions.push({
        questionId,
        answerScore: answer.score,
        weight,
        weightedScore,
      });
    }

    const percentage = maximum > 0 ? Math.round((rawScore / maximum) * 100) : 0;

    result[clusterId] = {
      clusterId,
      rawScore,
      maximum,
      percentage,
      contributingQuestions,
    };
  }

  return result;
}
